import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import classNames from 'classnames';
import { completeChatACW } from 'store/chat/chat.actions';
import { setRedirect } from 'store/global/global.actions';
import { setSelectedChat } from 'store/chat/chat.actions';
import useContactLogForm from 'hooks/useContactLogForm';
import useChatConnections from 'hooks/redux/useChatConnections';
import useAppConfig from 'hooks/redux/useAppConfig';
import useSize from 'hooks/useSize';
import useHasFeature, { AppFeatures, useGetFeatureConfig } from 'hooks/useHasFeature';
import { setCSSVar, isContactLogEmpty } from 'utils';
import CustomerInputs from 'views/Home/CustomerInputs';
import CompleteWrapUp from 'views/Home/ContactLog/complete-wrap-up';
import { ContactPanel } from 'views/ContactPanel';
import ContactLog from 'views/Home/ContactLog';
import CallHistory from 'views/Home/CallHistory';
import DPA from 'views/Home/DPA';
import Tabs, { SATab } from 'components/Tabs';
import ChatController from './ChatController';
import ChatMessages from './ChatMessenger/ChatMessages';
import ChatMessengerControls from './ChatMessenger/ChatMessengerControls';
import './chat.scss';
import RootState from 'store/state';
import SocialChatMessages from './ChatMessenger/SocialChatMessages';
import { removeContactHistory } from 'store/contactHistory/contactHistory.actions';
import ActiveChatMessageView from './ChatMessenger/ActiveChatMessageView';

interface Props { }

const generateTabs = (acw?: boolean, alwaysShowContactLog?: boolean) => {
  const chatTab = {
    id: 'chat',
    content: 'Chat',
  };
  const contactLogTab = {
    id: 'log',
    content: 'Contact Log',
  };
  if (!acw && !alwaysShowContactLog) return [chatTab];
  return [chatTab, contactLogTab];
};

const Chat: React.FC<Props> = () => {
    const selectedChat = useChatConnections("selected");
    const chats = useChatConnections();
    const [tab, setTab] = useState("chat");
    const hasFeature = useHasFeature();
    const contactLog = useContactLogForm();
    const noContactLogOrEmpty = isContactLogEmpty(contactLog);
    const appConfig = useAppConfig();

    const chatFeatures = useGetFeatureConfig()(AppFeatures.CHAT);

    const uniqueSocialId = useSelector((state: RootState) => state.contact?.attributes['sa-customer-endpoint-id']);

    //chat panel size
    const { size: { width }, ref } = useSize<HTMLDivElement>();

    const dispatch = useDispatch();

    useEffect(() => {
        setCSSVar('--sa-quick-replies-left', width + "px");
        if (chatFeatures?.chatPanelWidthPercent) {
            setCSSVar('--sa-chat-panel-width', chatFeatures.chatPanelWidthPercent + "%");
        }

    }, [width, chatFeatures])

    useEffect(() => {
        if (selectedChat) return;
        //Try route to another chat
        const nonConnectingChats = chats.filter(
            c => c.status !== connect.ContactStateType.CONNECTING
        );
        if (nonConnectingChats.length) {
            dispatch(setSelectedChat(nonConnectingChats[0].id));
        } else {
            dispatch(setRedirect('/chat'));
        }
    }, [selectedChat]);

    useEffect(() => {
        if (selectedChat?.acw) {
            setTab('log');
        } else {
            setTab('chat');
        }
    }, [selectedChat?.acw]);

    const onEndACW = () => {
        // Temporary batch wrap until we've fully migrated to React 18
        batch(() => {
            uniqueSocialId && dispatch(removeContactHistory('CHAT', uniqueSocialId));
            dispatch(completeChatACW(selectedChat?.id!));
        });
    }

    const alwaysShowContactLog = appConfig?.alwaysShowContactLog || false;
    const ChatTabs: SATab[] = useMemo(() => generateTabs(selectedChat?.acw, alwaysShowContactLog), [selectedChat]);

    return (
        <>
            <div className="row chat-home">
                <div ref={ref} className={classNames({
                    "chat-panel panel fixed-height row column": true,
                    "acw": selectedChat?.acw
                })}>
                    {!!selectedChat ? (
                        <>
                            <ChatController />

                            {hasFeature(AppFeatures.DPA) && <DPA />}
                            {hasFeature(AppFeatures.CUSTOMER_INPUTS) && <CustomerInputs />}

                            <Tabs className="sa-chat-tabs" selected={tab} tabs={ChatTabs} onClick={setTab} />
                            {tab === "chat" && (
                                <>
                                    {hasFeature(AppFeatures.SOCIAL_CHAT_MESSAGES) &&
                                    selectedChat.socialMediaPlatform ? (
                                      <SocialChatMessages />
                                    ) : (
                                        <ActiveChatMessageView />
                                    )}     
                                    <ChatMessengerControls />
                                </>
                            )}

                            {tab === "log" && (
                                <>
                                    <ContactLog channel="CHAT" hideHeader acw={selectedChat?.acw} onSubmit={onEndACW} />
                                    {noContactLogOrEmpty && (
                                        <div className="md-mar">
                                            <CompleteWrapUp onComplete={onEndACW} />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        hasFeature(AppFeatures.CALL_HISTORY) && <CallHistory channel="CHAT" />
                    )}
                </div>
                <ContactPanel channel="CHAT" />
            </div>
        </>
    )
}

export default Chat;
